import { AxiosResponse } from 'axios';
import imageCompression from 'browser-image-compression';
import api from '~/services/api';
import jszip from 'jszip';
import {
  paramsProps,
  getByIdProps,
  getProductQuotaProps,
  getProductRestrictionProps,
  getListProps,
  getProductWeightProps,
  getProductBatchProps
} from './product-interface';
import { ProductQuotaInterface, ProductBatchInterface } from '@e3labs/isa-data-dictionary';
// import { interceptorToken } from '~/services/api';

// api.interceptors.request.use(interceptorToken);

class ProductService {
  getProducts(skip: number = 0, limit: number = 100): Promise<AxiosResponse<any[] | null>> {
    return api.get<any[]>('/product', {
      params: {
        skip,
        limit,
        fieldsToShow: 'externalCode,name,pictures,brand,stocks,prices'
      }
    });
  }

  oldGetQuery(
    { fieldsToShow, skip, limit = 100, queryString, filters, productExternalCodes }: paramsProps,
    opts?: any
  ): Promise<AxiosResponse<any[] | null>> {
    return api.get('/product', {
      params: {
        skip,
        limit,
        fieldsToShow,
        queryString,
        productExternalCodes,
        ...filters
      },
      ...opts
    });
  }

  getQuery(
    { fieldsToShow, skip, limit = 100, queryString, filters, productExternalCodes }: paramsProps,
    data: any,
    opts?: any
  ): Promise<AxiosResponse<any[] | null>> {
    return api.post(
      '/product',
      {
        ...data
      },
      {
        params: {
          skip,
          limit,
          fieldsToShow,
          queryString,
          productExternalCodes,
          ...filters
        },
        ...opts
      }
    );
  }

  getRawProducts(
    { fieldsToShow, skip, limit, queryString, productExternalCodes, filters }: paramsProps,
    opts?: any
  ): Promise<AxiosResponse<any[] | null>> {
    return api.get('/product/rawlist', {
      params: {
        skip,
        limit,
        fieldsToShow,
        queryString,
        productExternalCodes,
        ...filters
      },
      ...opts
    });
  }

  getListProducts(
    { queryString, skip = 0, limit = 100, ...filters }: getListProps,
    opts: any
  ): Promise<AxiosResponse<any[] | null>> {
    return api.get('/product/products-default-price', {
      params: {
        queryString,
        skip,
        limit,
        ...filters
      },
      ...opts
    });
  }

  getListProductsCount({ queryString, ...filters }: getListProps, opts: any): Promise<AxiosResponse<any[] | null>> {
    return api.get('/product/products-default-price-count', {
      params: {
        queryString,
        ...filters
      },
      ...opts
    });
  }

  getProductsCount({ queryString, ...filters }: any, opts: any): Promise<AxiosResponse<any[] | null>> {
    return api.get('/product/count', {
      params: {
        queryString,
        ...filters
      },
      ...opts
    });
  }

  getById(params: any): Promise<AxiosResponse<any | null>> {
    return api.get(`/product/${params._id}`, {
      params: {
        ...(params.subsidiaryExternalCode && {
          subsidiaryExternalCode: params.subsidiaryExternalCode
        }),
        ...params
      }
    });
  }

  getProductQuota(params: getProductQuotaProps): Promise<AxiosResponse<ProductQuotaInterface[]>> {
    return api.get(`/product/quota`, { params: { ...params } });
  }

  //Endpoint legado, getSalesRestriction é o mais atual
  getProductRestriction(params: getProductRestrictionProps): Promise<AxiosResponse<any | null>> {
    return api.get(`/product/sales-restriction`, { params: { ...params } });
  }

  getSalesRestriction(params: getProductRestrictionProps): Promise<AxiosResponse<any | null>> {
    return api.get(`/sales-restriction`, { params: { ...params } });
  }

  getProductAlternatives(params: any): Promise<AxiosResponse<any | null>> {
    return api.get('/product/product-alternative', { params: { ...params } });
  }

  async postPictures(pictures: File[]) {
    const ZIP_FORMAT = ['application/x-zip-compressed', 'application/zip'];

    const options = {
      maxWidthOrHeight: 350,
      useWebWorker: true
    };

    const itemsZip = pictures?.filter(picture => ZIP_FORMAT.includes(picture.type));
    let newPictures: any = [...pictures?.filter(picture => !ZIP_FORMAT.includes(picture.type))];

    const zipObject = new jszip();
    const formData = new FormData();

    if (itemsZip?.length) {
      for (const item of itemsZip) {
        const zips: any = await zipObject.loadAsync(item);

        for (const zip of Object.keys(zips.files)) {
          const content = await zipObject.file(zips[zip])?.async('blob');

          if (content) {
            newPictures.push(new File([content], zips[zip]));
          }
        }
      }
    }

    await Promise.all(
      newPictures?.map(picture =>
        imageCompression(picture, options).then((compressedImage: any) => {
          zipObject.file(picture.name, compressedImage);
        })
      )
    );

    const zipFileInBlob = await zipObject.generateAsync({ type: 'blob' });

    formData.append('file', zipFileInBlob, 'compressedImages.zip');

    return await api.put('/product/configuration/pictures', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  getPromotionKitList(params: any, opts?: any): Promise<AxiosResponse<any | null>> {
    return api.get('/promotion/kit/available', { params: { ...params }, ...opts });
  }

  getPromotionKitListCount(params: any, opts?: any): Promise<AxiosResponse<any | null>> {
    return api.get('/promotion/kit/available', { params: { ...params, count: true }, ...opts });
  }

  getProductWeight({
    netWeight = 0,
    packingQuantity = 1,
    soldQuantity = 1,
    showTotalProductWeight
  }: getProductWeightProps): number {
    return showTotalProductWeight ? netWeight * packingQuantity * soldQuantity : packingQuantity * soldQuantity;
  }

  getBatchValityList(params: getProductBatchProps): Promise<AxiosResponse<ProductBatchInterface | null>> {
    return api.get('/product/validity', { params: { ...params } });
  }
}

export default new ProductService();
